import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBrand, BRAND_COLORS } from '../../contexts/BrandContext';
import { FeatureGate, useFeatureGate } from '../../components/FeatureGate';
import { Make_Authorized_API_Call, isSuccessResponse } from '../../api/apiClient';
import { API_ENDPOINTS } from '../../services/api/endpoints';
import { useStatus } from '../../contexts/StatusProvider';
import CPODashboardWidget from '../../components/CPODashboardWidget';
import { getSpecMetrics, type SpecMetrics } from '../../api/specifications';
import OnboardingWizard from '../../components/OnboardingWizard';

interface Specification {
  id: string;
  title: string;
  status: 'draft' | 'in_review' | 'approved' | 'outdated';
  template: string;
  lastModified: string;
  linkedCode: boolean;
}

interface DriftAlert {
  id: string;
  specId: string;
  specTitle: string;
  type: 'code_changed' | 'dependency_updated' | 'review_needed';
  severity: 'info' | 'warning' | 'critical';
  message: string;
  timestamp: string;
}

interface UsageStats {
  specsCreated: number;
  specsApproved: number;
  scansRemaining: number;
  scansUsed: number;
}

const PowerSpecDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { brandCode, isFeatureEnabled } = useBrand();
  const { tier } = useFeatureGate('spec_library');
  const navigate = useNavigate();
  const { Client_ID: statusClientId, User_Entitlements } = useStatus();

  // Get client ID: prefer StatusProvider (from entitlements), fallback to localStorage
  const clientId = statusClientId
    || localStorage.getItem('selectedClientId')
    || (User_Entitlements?.[0] as any)?.Client_ID
    || '';

  // Brand theming for onboarding wizard
  const brandInfo = BRAND_COLORS[brandCode] || BRAND_COLORS.equilateral;

  const [recentSpecs, setRecentSpecs] = useState<Specification[]>([]);
  const [driftAlerts, setDriftAlerts] = useState<DriftAlert[]>([]);
  const [cpoMetrics, setCpoMetrics] = useState<SpecMetrics | null>(null);
  const [usageStats, setUsageStats] = useState<UsageStats>({
    specsCreated: 0,
    specsApproved: 0,
    scansRemaining: 0,
    scansUsed: 0,
  });

  const fetchDashboardData = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch recent specifications from API
        const specsResponse = await Make_Authorized_API_Call<{
          Specification_ID: string;
          Title: string;
          Status: string;
          Template_Type: string;
          Updated_At: string;
          Repository_Link: string | null;
        }>(API_ENDPOINTS.SPECIFICATIONS.LIST, 'GET', undefined, { params: { Client_ID: clientId, limit: '4' } });

        if (isSuccessResponse(specsResponse)) {
          const specs = specsResponse.data.Records.map(spec => ({
            id: spec.Specification_ID,
            title: spec.Title,
            status: (spec.Status?.toLowerCase() || 'draft') as Specification['status'],
            template: spec.Template_Type || 'User Story',
            lastModified: formatTimeAgo(spec.Updated_At),
            linkedCode: !!spec.Repository_Link,
          }));
          setRecentSpecs(specs);
        } else {
          throw new Error(specsResponse.message || 'Failed to load specifications');
        }

        // Fetch CPO metrics from API
        if (clientId) {
          try {
            const metricsResponse = await getSpecMetrics(clientId);
            if (isSuccessResponse(metricsResponse)) {
              const metrics = metricsResponse.data.Records?.[0];
              if (metrics) {
                setCpoMetrics(metrics);
                // Map CPO metrics to usage stats
                setUsageStats({
                  specsCreated: metrics.Total_Specifications || 0,
                  specsApproved: 0, // Would need separate query
                  scansRemaining: 0, // Would come from entitlements
                  scansUsed: metrics.Total_Executions || 0,
                });
              }
            }
          } catch (metricsErr) {
            console.warn('[PowerSpecDashboard] CPO metrics not available:', metricsErr);
            // CPO metrics are optional - don't fail the whole dashboard
          }
        }

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data';
        console.error('[PowerSpecDashboard] Error:', errorMessage);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
  }, [clientId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Show onboarding wizard when user has zero specs and data is loaded
  useEffect(() => {
    if (!loading && !error && recentSpecs.length === 0 && usageStats.specsCreated === 0) {
      setShowOnboarding(true);
    }
  }, [loading, error, recentSpecs, usageStats.specsCreated]);

  const handleOnboardingComplete = useCallback(() => {
    setShowOnboarding(false);
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatTimeAgo = (timestamp: string): string => {
    const now = Date.now();
    const then = new Date(timestamp).getTime();
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'in_review': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'outdated': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeverityStyle = (severity: string) => {
    switch (severity) {
      case 'critical': return 'border-red-200 bg-red-50';
      case 'warning': return 'border-yellow-200 bg-yellow-50';
      case 'info': return 'border-blue-200 bg-blue-50';
      default: return 'border-gray-200 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading specifications...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            <p className="font-semibold">Error loading dashboard</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show onboarding wizard for first-run users with zero specs
  if (showOnboarding) {
    return (
      <OnboardingWizard
        brandName={brandInfo.name}
        brandColor={brandInfo.primary}
        onComplete={handleOnboardingComplete}
        clientId={clientId}
      />
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">PowerSpec Dashboard</h1>
            <p className="text-gray-600">Spec-driven development for your team</p>
          </div>
          <div className="flex gap-3">
            <Link
              to="/specs"
              className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors font-medium"
            >
              Open Spec Library
            </Link>
            <Link
              to="/specs?mode=create"
              className="px-4 py-2 border border-violet-600 text-violet-600 rounded-lg hover:bg-violet-50 transition-colors font-medium"
            >
              New Specification
            </Link>
          </div>
        </div>

        {/* Usage Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-600 mb-2">Specs Created</div>
            <div className="text-3xl font-bold text-gray-900">{usageStats.specsCreated}</div>
            <div className="text-xs text-gray-500 mt-1">this month</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-600 mb-2">Approved Specs</div>
            <div className="text-3xl font-bold text-green-600">{usageStats.specsApproved}</div>
            <div className="text-xs text-gray-500 mt-1">ready for development</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-600 mb-2">Drift Alerts</div>
            <div className="text-3xl font-bold text-yellow-600">{driftAlerts.length}</div>
            <div className="text-xs text-gray-500 mt-1">need attention</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="text-sm text-gray-600 mb-2">Scans Remaining</div>
            <div className="text-3xl font-bold text-violet-600">{usageStats.scansRemaining}</div>
            <div className="text-xs text-gray-500 mt-1">
              {tier === 'free' ? 'of 10 this month' : 'of 100 this month'}
            </div>
          </div>
        </div>

        {/* CPO Dashboard Widget - Real Cost Data */}
        {clientId && (
          <div className="mb-8">
            <CPODashboardWidget
              clientId={clientId}
              onViewDetails={() => navigate('/specs')}
            />
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-8">
          {/* Recent Specifications */}
          <div className="md:col-span-2 bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Specifications</h2>
              <Link to="/specs" className="text-sm text-violet-600 hover:text-violet-700">
                View all
              </Link>
            </div>
            <div className="space-y-3">
              {recentSpecs.map((spec) => (
                <Link
                  key={spec.id}
                  to={`/specs/${spec.id}`}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{spec.title}</span>
                      {spec.linkedCode && (
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {spec.template} - Modified {spec.lastModified}
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(spec.status)}`}>
                    {spec.status.replace('_', ' ')}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Drift Alerts */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Drift Alerts</h2>
            {driftAlerts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>All specs are in sync</p>
              </div>
            ) : (
              <div className="space-y-3">
                {driftAlerts.map((alert) => (
                  <Link
                    key={alert.id}
                    to={`/specs/${alert.specId}`}
                    className={`block p-3 border rounded-lg ${getSeverityStyle(alert.severity)}`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <span className="font-medium text-gray-900 text-sm">{alert.specTitle}</span>
                      <span className="text-xs text-gray-500">{alert.timestamp}</span>
                    </div>
                    <p className="text-sm text-gray-600">{alert.message}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* AI Embellishment Promo (Free tier) */}
        <FeatureGate
          feature="ai_embellishment"
          gateType="soft"
          upgradeMessage="AI-powered embellishment helps you write complete specs with suggested acceptance criteria, edge cases, and test scenarios."
        >
          <div className="mt-8 bg-gradient-to-r from-violet-600 to-violet-800 rounded-xl shadow-sm p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold mb-2">AI Embellishment Available</h2>
                <p className="text-violet-100">
                  Your Pro plan includes AI-powered spec embellishment. Let AI suggest acceptance criteria, edge cases, and test scenarios.
                </p>
              </div>
              <Link
                to="/specs?mode=create"
                className="px-4 py-2 bg-white text-violet-700 rounded-lg hover:bg-violet-50 transition-colors font-medium"
              >
                Create AI-Enhanced Spec
              </Link>
            </div>
          </div>
        </FeatureGate>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/specs?mode=create" className="p-4 border rounded-lg hover:bg-gray-50 text-left">
              <div className="font-medium text-gray-900">New Specification</div>
              <div className="text-xs text-gray-500">From template or blank</div>
            </Link>
            <FeatureGate feature="codebase_scanning" gateType="hard">
              <Link to="/specs/scan" className="p-4 border rounded-lg hover:bg-gray-50 text-left">
                <div className="font-medium text-gray-900">Scan Codebase</div>
                <div className="text-xs text-gray-500">Discover undocumented features</div>
              </Link>
            </FeatureGate>
            <Link to="/specs?status=in_review" className="p-4 border rounded-lg hover:bg-gray-50 text-left">
              <div className="font-medium text-gray-900">Review Queue</div>
              <div className="text-xs text-gray-500">Specs awaiting approval</div>
            </Link>
            <FeatureGate feature="dependency_matrix" gateType="hard">
              <Link to="/specs/dependencies" className="p-4 border rounded-lg hover:bg-gray-50 text-left">
                <div className="font-medium text-gray-900">Dependency Matrix</div>
                <div className="text-xs text-gray-500">Spec relationships</div>
              </Link>
            </FeatureGate>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PowerSpecDashboard;
